import ApiGetOptions from '@/datatypes/api-get-options';
import ApiPutOptions from '@/datatypes/api-put-options';
import ApiDeleteOptions from '@/datatypes/api-delete-options';
import ApiPostOptions from '@/datatypes/api-post-options';
import ApiService from '@/services/api/ApiService';
import decamelizeKeysDeep from 'decamelize-keys-deep';

export default class EnterpriseService extends ApiService {
  public getEnterprises(options) {
    return this.call(new ApiGetOptions(
      {
        url: '/enterprises',
        params: options,
      },
    ));
  }

  public create(params) {
    return this.call(new ApiPostOptions(
      {
        url: '/enterprises',
        data: decamelizeKeysDeep(params),
      },
    ));
  }

  public getEnterprise(enterpriseId) {
    return this.call(new ApiGetOptions(
      {
        url: `/enterprises/${enterpriseId}`,
      },
    ));
  }

  public getUnbilledEnterprises(options) {
    return this.call(new ApiGetOptions(
      {
        url: '/enterprises/unbilled',
        params: options,
      },
    ));
  }

  public transferOwnership(enterpriseId, userId) {
    return this.call(new ApiPutOptions(
      {
        url: `/enterprises/${enterpriseId}/ownership`,
        data: decamelizeKeysDeep({ userId }),
      },
    ));
  }

  public uploadDocument(enterpriseId, params) {
    return this.call(new ApiPostOptions(
      {
        url: `/enterprises/${enterpriseId}/enterprise_documents`,
        data: params,
      },
    ));
  }

  public downloadDocument(enterpriseId, documentId) {
    return this.call(new ApiPostOptions(
      {
        url: `/enterprises/${enterpriseId}/enterprise_documents/${documentId}/download`,
      },
    ));
  }

  public deleteDocument(enterpriseId, documentId) {
    return this.call(new ApiDeleteOptions(
      {
        url: `/enterprises/${enterpriseId}/enterprise_documents/${documentId}`,
      },
    ));
  }

  public archiveDocument(enterpriseId, documentId) {
    return this.call(new ApiPutOptions(
      {
        url: `/enterprises/${enterpriseId}/enterprise_documents/${documentId}`,
        params: {
          archived: true,
        },
      },
    ));
  }

  public unarchiveDocument(enterpriseId, documentId) {
    return this.call(new ApiPutOptions(
      {
        url: `/enterprises/${enterpriseId}/enterprise_documents/${documentId}`,
        params: {
          archived: false,
        },
      },
    ));
  }

  public getEnterpriseUsers(enterpriseId) {
    return this.call(new ApiGetOptions(
      {
        url: `/enterprises/${enterpriseId}/users`,
      },
    ));
  }

  public getGlobalAdministrators() {
    return this.call(new ApiGetOptions(
      {
        url: '/administrators',
      },
    ));
  }

  public deleteGlobalAdministrator(user) {
    return this.call(new ApiDeleteOptions(
      {
        url: `/administrators/${user.id}`,
      },
    ));
  }

  public removeUserMfa(user) {
    return this.call(new ApiDeleteOptions(
      {
        url: `/users/${user.id}/mfa`,
      },
    ));
  }

  public addGlobalAdministrator(user) {
    return this.call(new ApiPostOptions(
      {
        url: `/administrators/${user.id}`,
      },
    ));
  }

  public migrateEnterprise(sourceEnterpriseId, destinationEnterpriseId) {
    return this.call(new ApiPostOptions(
      {
        url: `/enterprises/${sourceEnterpriseId}/migration`,
        params: decamelizeKeysDeep({
          destinationEnterpriseId,
        }),
      },
    ));
  }

  public confirmOperationRequest(operationRequestId, code) {
    return this.call(new ApiPostOptions(
      {
        url: `/operation_requests/${operationRequestId}/confirmation`,
        data: {
          code,
        },
      },
    ));
  }

  public synchroniseSubscription(enterpriseId) {
    return this.call(new ApiPostOptions(
      {
        url: `/enterprises/${enterpriseId}/subscription/synchronisation`,
        data: {},
      },
    ));
  }

  public getEnterpriseDocuments(enterpriseId, params) {
    return this.call(new ApiGetOptions(
      {
        url: `/enterprises/${enterpriseId}/enterprise_documents`,
        params,
      },
    ));
  }

  public updateEnterpriseUser(enterpriseId, userId, params) {
    return this.call(new ApiPutOptions(
      {
        url: `/enterprises/${enterpriseId}/users/${userId}`,
        data: decamelizeKeysDeep(params),
      },
    ));
  }

  public changeEnterpriseLevel(enterpriseId, params) {
    return this.call(new ApiPutOptions(
      {
        url: `/enterprises/${enterpriseId}/level`,
        data: decamelizeKeysDeep(params),
      },
    ));
  }

  public changeUserLevel(enterpriseId, userId, params) {
    return this.call(new ApiPutOptions(
      {
        url: `/enterprises/${enterpriseId}/users/${userId}/level`,
        data: decamelizeKeysDeep(params),
      },
    ));
  }

  public getOveragePrice(priceId) {
    return this.call(new ApiGetOptions(
      {
        url: `/enterprises/overage_prices/${priceId}`,
      },
    ));
  }

  public invoiceEnterprise(enterpriseId) {
    return this.call(new ApiPostOptions(
      {
        url: `/enterprises/${enterpriseId}/invoice`,
      },
    ));
  }

  public getEnterpriseStats(enterpriseId) {
    return this.call(new ApiGetOptions(
      {
        url: `/enterprises/${enterpriseId}/stats`,
      },
    ));
  }

  public updateEnterprise(enterpriseId, params) {
    return this.call(new ApiPutOptions(
      {
        url: `/enterprises/${enterpriseId}`,
        data: decamelizeKeysDeep(params),
      },
    ));
  }

  public updateEnterpriseLimits(enterpriseId, params) {
    return this.call(new ApiPutOptions(
      {
        url: `/enterprises/${enterpriseId}/limits`,
        data: decamelizeKeysDeep(params),
      },
    ));
  }

  public createBillingCustomer(enterpriseId, params) {
    return this.call(new ApiPostOptions(
      {
        url: `/enterprises/${enterpriseId}/billing_customer`,
        data: decamelizeKeysDeep(params),
      },
    ));
  }

  public createBillingSubscription(enterpriseId, params) {
    return this.call(new ApiPostOptions(
      {
        url: `/enterprises/${enterpriseId}/billing_subscription`,
        data: decamelizeKeysDeep(params),
      },
    ));
  }
}
